import { useEffect, useState } from "react";
import { Mail, MailCheck, Users2, Loader2, Megaphone, CheckCircle2, RotateCcw } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";


// Suit un job d'envoi (progression) jusqu'à son terme.
export function useEmailJob(initial) {
  const [result, setResult] = useState(initial);
  useEffect(() => { setResult(initial); }, [initial]);
  useEffect(() => {
    if (!result?.job_id || result.status !== "running") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/email-jobs/${result.job_id}`);
        setResult((r) => ({ ...r, ...data }));
      } catch { /* on réessaie au prochain tick */ }
    }, 2000);
    return () => clearInterval(t);
  }, [result?.job_id, result?.status]);
  return [result, setResult];
}

// Récapitulatif réel d'un envoi (élèves / responsables / envoyées / en attente / sans e-mail / échecs) + progression
// + renvoi des seuls échecs (avec confirmation).
export function SendSummary({ result: initial, testid = "send-summary" }) {
  const [result, setResult] = useEmailJob(initial);
  const [retrying, setRetrying] = useState(false);
  const [confirmRetry, setConfirmRetry] = useState(false);
  const running = result.status === "running";
  const pct = result.recipients ? Math.round(((result.processed || 0) / result.recipients) * 100) : 100;
  const cells = [
    { v: result.students, l: "élèves", cls: "bg-slate-50 text-slate-700 dark:bg-white/5", t: "students" },
    { v: result.recipients, l: "responsables avec e-mail", cls: "bg-sky-50 text-sky-700 dark:bg-sky-500/10", t: "recipients" },
    { v: result.sent, l: "envoyés", cls: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10", t: "sent" },
    { v: result.pending, l: "en attente", cls: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10", t: "pending" },
    { v: result.missing, l: "sans adresse e-mail", cls: "bg-amber-50 text-amber-600 dark:bg-amber-500/10", t: "missing" },
    { v: result.failed, l: "échecs", cls: "bg-rose-50 text-rose-600 dark:bg-rose-500/10", t: "failed" },
  ];

  const retryFailed = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post(`/email-jobs/${result.job_id}/retry-failed`);
      toast.success(`${data.retried} e-mail${data.retried > 1 ? "s" : ""} remis en file d'attente.`);
      setConfirmRetry(false);
      setResult((r) => ({ ...r, ...data }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRetrying(false);
    }
  };
  return (
    <div className="flex flex-col gap-2 pt-1" data-testid={testid} data-status={result.status || "done"}>
      {running && (
        <div className="rounded-xl border border-sky-100 bg-sky-50 p-3 text-xs text-sky-800 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-200" data-testid="send-progress">
          <p className="mb-1.5 flex items-center gap-1.5 font-medium">
            <Loader2 size={13} className="animate-spin" /> Envoi en cours : {result.processed || 0} / {result.recipients}
            {result.waiting ? " — limite du fournisseur d'e-mail atteinte (10 e-mails par tranche de 10 minutes), reprise automatique ; vous pouvez fermer cette fenêtre, l'envoi continue même après un redémarrage." : "."}
          </p>
          <div className="h-1.5 overflow-hidden rounded-full bg-sky-100 dark:bg-white/10">
            <div className="h-full rounded-full bg-sky-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}
      {result.status === "interrupted" && (
        <p className="rounded-xl bg-amber-50 p-3 text-xs text-amber-700 dark:bg-amber-500/10" data-testid="send-interrupted">
          L'envoi a été interrompu par un redémarrage du serveur. Utilisez « Notifier → Aux parents n'ayant pas signé » pour relancer les envois manquants.
        </p>
      )}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {cells.map((c) => (
          <div key={c.l} className={`rounded-xl p-3 text-center ${c.cls}`} data-testid={`summary-${c.t}`}>
            <p className="text-2xl font-semibold">{c.v ?? 0}</p>
            <p className="text-xs text-slate-500">{c.l}</p>
          </div>
        ))}
      </div>
      {result.duplicates > 0 && (
        <p className="text-xs text-slate-500" data-testid="send-duplicates">
          {result.duplicates} e-mail{result.duplicates > 1 ? "s" : ""} déjà en file d'attente (non dupliqué{result.duplicates > 1 ? "s" : ""}).
        </p>
      )}
      {!running && result.failed > 0 && result.job_id && (
        <AlertDialog open={confirmRetry} onOpenChange={(v) => { if (!retrying) setConfirmRetry(v); }}>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="self-start rounded-xl" data-testid="retry-failed-button">
              <RotateCcw size={15} className="mr-1.5" /> Renvoyer les échecs ({result.failed})
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="max-w-[calc(100%-2rem)] rounded-2xl sm:max-w-md" data-testid="retry-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Renvoyer les e-mails en échec ?</AlertDialogTitle>
              <AlertDialogDescription>
                {result.failed} e-mail{result.failed > 1 ? "s" : ""} en échec {result.failed > 1 ? "seront remis" : "sera remis"} en file d'attente.
                Les e-mails déjà envoyés avec succès ne seront pas renvoyés.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-xl" disabled={retrying} data-testid="retry-cancel">Annuler</AlertDialogCancel>
              <AlertDialogAction className="rounded-xl" disabled={retrying} onClick={(e) => { e.preventDefault(); retryFailed(); }} data-testid="retry-confirm">
                {retrying ? <Loader2 size={16} className="animate-spin" /> : "Renvoyer"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {result.errors?.length > 0 && (
        <ul className="flex max-h-48 flex-col gap-2 overflow-y-auto rounded-xl bg-rose-50 p-3 text-xs text-rose-700 dark:bg-rose-500/10" data-testid="send-errors">
          {result.errors.map((e, i) => (
            <li key={i} data-testid={`send-error-${i}`}>
              <p className="font-medium">Échec : {e.name}{e.guardian ? ` — ${e.guardian}` : ""}</p>
              {e.email && <p className="text-rose-600/80">Adresse : {e.email}</p>}
              {e.message && <p className="text-rose-600/80">Erreur : {e.message}{e.status ? ` (HTTP ${e.status})` : ""}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Notification par e-mail au niveau de la classe (non-signés / toutes les familles).
export function NotifyClassButton({ classId, offset = 0 }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null); // "unsigned" | "all"
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setTarget(null); setLoading(false); setResult(null); };

  const send = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/classes/${classId}/notify`, { target, offset });
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => { reset(); setOpen(true); }}
        className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-600 transition-transform hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
        data-testid="notify-class-button"
      >
        <Mail size={15} /> <span className="hidden sm:inline">Notifier</span>
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="rounded-2xl sm:max-w-[420px]" data-testid="notify-class-dialog">
          <DialogHeader>
            <DialogTitle>{result ? "Récapitulatif de l'envoi" : "Notifier les familles par e-mail"}</DialogTitle>
            <DialogDescription>
              {(() => {
                if (result) return result.status === "running" ? "Envoi en cours : chaque responsable légal disposant d'une adresse reçoit son propre e-mail." : "Chaque responsable légal disposant d'une adresse a reçu son propre e-mail.";
                if (target === "unsigned") return "Envoyer un e-mail uniquement aux familles n'ayant pas encore signé le Passeport de la semaine ?";
                if (target === "all") return "Envoyer un e-mail à toutes les familles de la classe ?";
                return "Choisissez les destinataires. Chaque responsable légal disposant d'une adresse e-mail sera contacté.";
              })()}
            </DialogDescription>
          </DialogHeader>

          {result && (
            <>
              <SendSummary result={result} testid="notify-summary" />
              <DialogFooter>
                <Button className="rounded-xl" onClick={() => { setOpen(false); reset(); }} data-testid="notify-close">Fermer</Button>
              </DialogFooter>
            </>
          )}
          {!result && target && (
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-xl" onClick={reset} data-testid="notify-cancel">
                Retour
              </Button>
              <Button className="rounded-xl" disabled={loading} onClick={send} data-testid="notify-confirm">
                {loading ? <Loader2 size={16} className="animate-spin" /> : "Confirmer l'envoi"}
              </Button>
            </DialogFooter>
          )}
          {!result && !target && (
            <div className="flex flex-col gap-2.5 pt-1">
              <button
                onClick={() => setTarget("unsigned")}
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition-transform hover:scale-[0.99] dark:border-white/10 dark:bg-white/5"
                data-testid="notify-unsigned-option"
              >
                <MailCheck size={20} className="text-amber-500" />
                <div>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Aux parents n'ayant pas signé</p>
                  <p className="text-xs text-slate-500">Rappel pour le Passeport de la semaine.</p>
                </div>
              </button>
              <button
                onClick={() => setTarget("all")}
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition-transform hover:scale-[0.99] dark:border-white/10 dark:bg-white/5"
                data-testid="notify-all-option"
              >
                <Users2 size={20} className="text-sky-500" />
                <div>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">À toutes les familles</p>
                  <p className="text-xs text-slate-500">Informer toute la classe.</p>
                </div>
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// Publication du Passeport de la semaine : rend visible côté parent + envoie les e-mails.
export function PublishButton({ classId, offset = 0, published, onPublished }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const openDialog = () => { setResult(null); setOpen(true); };

  const publish = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/classes/${classId}/publish`, { offset });
      setResult(data);
      onPublished?.();
      toast.success(data.status === "running" ? "Passeports publiés — envoi des notifications en cours" : `Passeports publiés — ${data.sent} notification${data.sent > 1 ? "s" : ""} envoyée${data.sent > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {published && (
        <span
          className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 py-2 text-xs font-medium text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
          data-testid="published-badge"
        >
          <CheckCircle2 size={15} /> Passeports publiés
        </span>
      )}
      <button
        onClick={openDialog}
        className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition-transform hover:scale-95 dark:bg-white dark:text-slate-900"
        data-testid="publish-button"
      >
        <Megaphone size={15} />
        <span className="hidden sm:inline">{published ? "Republier / renvoyer" : "Publier le Passeport de la semaine"}</span>
        <span className="sm:hidden">Publier</span>
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setResult(null); }}>
        <DialogContent className="rounded-2xl sm:max-w-[440px]" data-testid="publish-dialog">
          {result ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle2 size={20} className="text-emerald-500" /> Passeports publiés
                </DialogTitle>
                <DialogDescription>Récapitulatif de l'envoi : chaque responsable légal avec e-mail reçoit sa propre notification.</DialogDescription>
              </DialogHeader>
              <SendSummary result={result} testid="publish-summary" />
              <DialogFooter>
                <Button className="rounded-xl" onClick={() => setOpen(false)} data-testid="publish-close">Fermer</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Publier le Passeport de la semaine ?</DialogTitle>
                <DialogDescription>
                  Les Passeports de la semaine deviendront visibles par les familles et un e-mail sera
                  automatiquement envoyé aux responsables disposant d'une adresse e-mail.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2">
                <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)} data-testid="publish-cancel">
                  Annuler
                </Button>
                <Button className="rounded-xl" disabled={loading} onClick={publish} data-testid="publish-confirm">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : "Confirmer la publication"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// Notification par e-mail à une seule famille (depuis la fiche élève).
export function NotifyFamilyButton({ classId, studentId, studentName }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/classes/${classId}/notify`, { target: "one", student_id: studentId });
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => { setResult(null); setOpen(true); }}
        className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition-transform hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
        data-testid="notify-family-button"
      >
        <Mail size={16} /> <span className="hidden sm:inline">Envoyer à cette famille</span>
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setResult(null); }}>
        <DialogContent className="rounded-2xl sm:max-w-[420px]" data-testid="notify-family-dialog">
          <DialogHeader>
            <DialogTitle>{result ? "Récapitulatif de l'envoi" : "Envoyer un e-mail à la famille ?"}</DialogTitle>
            <DialogDescription>
              {result ? (
                <>Envoi aux responsables légaux de <strong>{studentName}</strong>.</>
              ) : (
                <>Un e-mail sera envoyé à chaque responsable légal de <strong>{studentName}</strong> disposant d'une adresse
                e-mail, concernant le Passeport de la semaine. Sans adresse enregistrée, aucun envoi ne sera effectué.</>
              )}
            </DialogDescription>
          </DialogHeader>
          {result ? (
            <>
              <SendSummary result={result} testid="notify-family-summary" />
              <DialogFooter>
                <Button className="rounded-xl" onClick={() => { setOpen(false); setResult(null); }} data-testid="notify-family-close">Fermer</Button>
              </DialogFooter>
            </>
          ) : (
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)} data-testid="notify-family-cancel">
                Annuler
              </Button>
              <Button className="rounded-xl" disabled={loading} onClick={send} data-testid="notify-family-confirm">
                {loading ? <Loader2 size={16} className="animate-spin" /> : "Confirmer l'envoi"}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
